import { useState, useEffect, useMemo } from 'react';
import { Globe, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { getBlockedCountries, blockCountry, unblockCountry, type BlockedCountry } from '@/lib/adminApi';

// ISO 3166-1 alpha-2 country codes with names
const ALL_COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (DRC)' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'São Tomé and Príncipe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

export function CountryBlockPanel() {
  const [blockedCountries, setBlockedCountries] = useState<BlockedCountry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<{ code: string; name: string } | null>(null);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    loadBlockedCountries();
  }, []);

  const loadBlockedCountries = async () => {
    try {
      setIsLoading(true);
      const data = await getBlockedCountries();
      setBlockedCountries(data);
    } catch (error: unknown) {
      console.error('Failed to load blocked countries:', error);
      // Don't show toast - table may not exist yet
    } finally {
      setIsLoading(false);
    }
  };

  const availableCountries = useMemo(() => {
    const blockedCodes = new Set(blockedCountries.map(c => c.country_code));
    return ALL_COUNTRIES.filter(c => !blockedCodes.has(c.code));
  }, [blockedCountries]);

  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return availableCountries.slice(0, 50); // Show first 50 by default
    const query = searchQuery.toLowerCase();
    return availableCountries.filter(
      c => c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query)
    );
  }, [availableCountries, searchQuery]);

  const handleBlockCountry = async () => {
    if (!selectedCountry) return;

    try {
      await blockCountry({
        countryCode: selectedCountry.code,
        countryName: selectedCountry.name,
        reason: blockReason || undefined,
      });
      await loadBlockedCountries();
      setDrawerOpen(false);
      setSelectedCountry(null);
      setBlockReason('');
      setSearchQuery('');
      toast.success(`${selectedCountry.name} blocked`);
    } catch (error: unknown) {
      console.error('Failed to block country:', error);
      toast.error((error as Error)?.message || 'Failed to block country');
    }
  };

  const handleUnblockCountry = async (country: BlockedCountry) => {
    if (!confirm(`Unblock ${country.country_name}?`)) return;

    try {
      await unblockCountry(country.country_code);
      await loadBlockedCountries();
      toast.success(`${country.country_name} unblocked`);
    } catch (error: unknown) {
      console.error('Failed to unblock country:', error);
      toast.error((error as Error)?.message || 'Failed to unblock country');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">Country Blocking</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDrawerOpen(true)}
          className="h-8 border-border"
        >
          <Plus className="w-3 h-3 mr-1" />
          Block Country
        </Button>
      </div>

      <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-400">
            Use country blocking with caution. This will deny access to all users from the blocked country,
            including legitimate users using VPNs or traveling.
          </p>
        </div>
      </div>

      {/* Blocked Countries List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        ) : blockedCountries.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            No countries blocked.
          </div>
        ) : (
          blockedCountries.map((country) => (
            <div
              key={country.country_code}
              className="flex items-center justify-between p-3 rounded-lg bg-red-500/5 border border-red-500/20"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{getFlagEmoji(country.country_code)}</span>
                <div>
                  <p className="text-sm font-medium text-white">
                    {country.country_name} ({country.country_code})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Blocked {new Date(country.blocked_at).toLocaleDateString()}
                    {country.reason && ` • ${country.reason}`}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleUnblockCountry(country)}
                className="h-8 text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Unblock
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Block Country Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCountry(null);
          setSearchQuery('');
          setBlockReason('');
        }}
        title="Block Country"
        description="Block all traffic from a specific country."
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Search Countries</Label>
            <div className="relative">
              <SearchInput
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder="Search by name or code..."
                className="bg-background border-border"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Select Country</Label>
            <div className="max-h-[200px] overflow-y-auto border border-border rounded-lg">
              {filteredCountries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No countries found
                </p>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => setSelectedCountry(country)}
                    className={`w-full flex items-center gap-3 p-2 hover:bg-muted/50 text-left ${
                      selectedCountry?.code === country.code ? 'bg-[#8FB560]/20' : ''
                    }`}
                  >
                    <span className="text-lg">{getFlagEmoji(country.code)}</span>
                    <span className="text-sm text-white">{country.name}</span>
                    <span className="text-xs text-muted-foreground">({country.code})</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {selectedCountry && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-white">
                Selected: {getFlagEmoji(selectedCountry.code)} {selectedCountry.name}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="e.g., High volume of attack traffic"
              className="bg-background border-border"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleBlockCountry}
              disabled={!selectedCountry}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              <Globe className="w-4 h-4 mr-2" />
              Block Country
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDrawerOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </AdminFormDrawer>
    </div>
  );
}

// Convert country code to flag emoji
function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
